import { StyledReactSelect } from '@fountain/fountain-ui-components';
import { Checkbox, Grid, Typography } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { useIntl } from 'react-intl';

import {
  buildRows,
  GroupRow,
  groupSelectionCount,
  MultiSelectGroup,
  MultiSelectRow,
  OptionRow,
  rowMatches,
  toggleGroupMembers,
  visibleMembers,
} from './groupedOptions';
import messages from './messages';
import { useStyles } from './styles';

/**
 * One entry in a set-membership picker.
 *
 * Deliberately not `ConditionSelectOption`, which also carries `isDisabled` for
 * the condition-type picker. Nothing here disables a row, and borrowing a type
 * with a field this control drops would let a caller set one that silently does
 * nothing.
 */
export interface SearchableMultiSelectOption {
  value: string;
  /** Falls back to the value — a funnel's title is nullable. */
  label?: string;
  /**
   * The `external_id` of the group this option sits under, joining to a
   * `groups` entry's `value`. Read only when `groups` is passed.
   */
  group?: string | null;
}

export interface SearchableMultiSelectProps {
  /** Lands on the input, so the row's controls stay individually addressable. */
  id: string;
  label: string;
  /** What the field holds, in the order it holds it. */
  values: string[];
  options: SearchableMultiSelectOption[];
  onChange: (values: string[]) => void;
  /**
   * The headers to file the options under, in the order they are shown.
   *
   * Passing this — even empty — puts the menu in grouped mode. Only the
   * locations condition is grouped; every other caller has no grouping to show,
   * and a header row an admin cannot use is a row in the way.
   */
  groups?: SearchableMultiSelectOption[];
  /** The message for this control, once the admin has tried to save. */
  error?: string;
  /**
   * Said when the server's cap cut the option list short, so a search over a
   * partial list is never read as a search over the account.
   */
  note?: string;
  /** The control's place in the row, which the row decides rather than this. */
  className?: string;
}

interface MultiSelectOptionProps {
  data: MultiSelectRow;
  innerProps: React.HTMLAttributes<HTMLDivElement>;
  innerRef: React.Ref<HTMLDivElement>;
  isFocused: boolean;
  isSelected: boolean;
  /**
   * react-select hands its own props down. Two are read: what the field holds,
   * and what the admin has typed — a header describes the part of its group that
   * survived the search, so it cannot be rendered without both.
   */
  selectProps: { value?: OptionRow[] | null; inputValue?: string };
}

/**
 * One row of the menu — an option, or a header standing for the options under
 * it.
 *
 * A checkbox rather than the design system's own right-hand check mark, on both
 * kinds of row. The header has three states and the design system's mark has
 * two, so a header cannot borrow it; and a menu that marked a header one way and
 * its members another would be showing one thing in two shapes. The checkbox is
 * the design system's own `Checkbox`, styled by the FDS theme — 16px box, brand
 * fill, white check, dash for a partial group — rather than an icon drawn here.
 *
 * It is inert: `aria-hidden` and out of the tab order. The row is the control,
 * and react-select drives it by click and by Enter on the focused row. A real
 * checkbox left in the accessibility tree would announce a second control per
 * row and put a tab stop inside a menu that has none.
 */
const MultiSelectOption: React.FC<MultiSelectOptionProps> = ({
  data,
  innerProps,
  innerRef,
  isFocused,
  isSelected,
  selectProps,
}) => {
  const styles = useStyles();

  if (data.kind === 'group') {
    const held = new Set((selectProps.value ?? []).map(row => row.value));
    const shown = visibleMembers(data, selectProps.inputValue ?? '');
    const chosen = groupSelectionCount(shown, held);
    // Checked means the whole group, so the three states map onto the design
    // system's checkbox as they are: box, dash, check.
    const all = shown.length > 0 && chosen === shown.length;

    return (
      <Grid
        {...innerProps}
        ref={innerRef}
        alignItems="center"
        className={clsx(styles.conditionGroupRow, { focused: isFocused })}
        container
        wrap="nowrap"
      >
        <Checkbox
          checked={all}
          className={styles.conditionOptionCheckbox}
          color="primary"
          disableRipple
          indeterminate={chosen > 0 && !all}
          inputProps={{ 'aria-hidden': true, readOnly: true, tabIndex: -1 }}
        />
        <Typography
          className={clsx(styles.conditionOptionLabel, 'group')}
          color="textPrimary"
          title={data.label}
          variant="body2"
        >
          {data.label}
        </Typography>
        {/* The count of what the header stands for right now, which is the set
            its checkbox describes and the set clicking it toggles. Under a
            search that is a subset of the group, so it is read off what is on
            screen rather than off the account's membership. */}
        <Typography className={styles.conditionGroupCount} variant="body2">
          {shown.length}
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid
      {...innerProps}
      ref={innerRef}
      alignItems="center"
      className={clsx(styles.conditionOptionRow, {
        focused: isFocused,
        indented: data.indented,
        selected: isSelected,
      })}
      container
      wrap="nowrap"
    >
      <Checkbox
        checked={isSelected}
        className={styles.conditionOptionCheckbox}
        color="primary"
        disableRipple
        inputProps={{ 'aria-hidden': true, readOnly: true, tabIndex: -1 }}
      />
      {/* `title` for the reason the design system's own option row carries one:
          an account names its own locations and its own people, and the control
          is narrow enough that a long name is read from the tooltip. */}
      <Typography
        className={styles.conditionOptionLabel}
        color="textPrimary"
        title={data.label}
        variant="body2"
      >
        {data.label}
      </Typography>
    </Grid>
  );
};

/**
 * Stable across renders, because react-select rebuilds its component map from
 * this object on every one: a type minted inside the render would unmount the
 * menu and drop the focused row on each keystroke.
 */
const SELECT_COMPONENTS = ({
  Option: MultiSelectOption,
} as unknown) as Record<string, React.ComponentType<unknown>>;

/**
 * A picker that holds a set — the condition rows' positions, openings and
 * locations, and a level's approver rosters.
 *
 * Filter-as-you-type, chips in the control, a per-chip remove and a clear-all,
 * which is what Design Requirements §3 and §4 both ask of a picker over a list
 * an account can grow to hundreds of rows.
 *
 * The multi sibling of `ConditionSelect`, and configured the same way for the
 * same reasons: `size="small"` to match the controls beside it, no `required`
 * asterisk on a surface where every control is, an empty placeholder because the
 * label already names the box — a placeholder is never a label. Read that
 * component's notes for those three.
 *
 * What is specific to holding a set:
 *
 * - `isClearable`, which is the design's clear-all. react-select turns it on for
 *   a multi by default; it is stated here because it is a requirement rather
 *   than an inheritance.
 * - `hideSelectedOptions={false}`. react-select drops a chosen option out of the
 *   menu for a multi, so the list shortens as you pick and the same row cannot be
 *   used to unpick. These lists are an account's own positions, locations and
 *   people, and a list that rearranges itself under the pointer is the worse
 *   trade.
 * - A stored value the option list does not carry still becomes a chip. The
 *   server caps each list, openings are active ones only and a user roster drops
 *   offboarded people, so a rule scoped to an opening that has since closed —
 *   or staffed by someone since offboarded — would otherwise lose that row the
 *   moment anyone opened the rule, silently widening a compliance gate on save.
 *
 * With `groups`, the menu also files its options under group headers. Grouping
 * takes the search over from react-select, because a header stands for the part
 * of its group the search left on screen: the count it shows, the state its
 * checkbox reads and the set it toggles all have to read that one list. It also
 * needs a matcher of its own — react-select's would run over a header's text,
 * which is a group name rather than an entity's, and drop every header.
 *
 * The row list itself is built from the options alone and kept across
 * keystrokes. react-select tracks the focused row by object identity, so a list
 * rebuilt per character would send the keyboard back to the top of the menu on
 * every one.
 *
 * A header selects its members individually. It never stores the group: a
 * group-valued condition is its own scope, chosen from its own picker, and it
 * keeps matching locations added to the group later — which a set of names
 * cannot do.
 *
 * Both messages are wired to the input with `aria-describedby` rather than
 * rendered beside it: the design system passes that attribute through to the
 * real input (AXHE-4666), so the note and the error are announced with the
 * control instead of being read only by someone who happens to move past them.
 */
export const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  id,
  label,
  values,
  options,
  onChange,
  groups,
  error,
  note,
  className,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const grouped = groups !== undefined;

  /**
   * What the admin has typed, held here rather than by react-select.
   *
   * Only grouped mode reads it. Flat mode leaves the search where react-select
   * keeps it, so the two pickers beside this one behave exactly as they did.
   */
  const [search, setSearch] = React.useState('');

  // Memoised on the caller's array for the reason `ConditionSelect` gives:
  // react-select takes a `setState` whenever `options` changes by identity, and
  // the panel re-renders on every keystroke elsewhere in the row.
  const resolved = React.useMemo(
    () =>
      options.map(option => ({
        value: option.value,
        label: option.label ?? option.value,
        group: option.group ?? null,
      })),
    [options],
  );

  const headers = React.useMemo(
    () =>
      (groups ?? []).map(
        (group): MultiSelectGroup => ({
          value: group.value,
          label: group.label ?? group.value,
        }),
      ),
    [groups],
  );

  /**
   * Built from the options alone, never from the search. react-select tracks the
   * focused row by object identity, so rows rebuilt on a keystroke would send
   * the keyboard back to the top of the menu mid-search — `buildRows` carries
   * the mechanism. `rowMatches` hides what the search excludes instead.
   */
  const rows = React.useMemo(
    (): MultiSelectRow[] =>
      grouped
        ? buildRows(resolved, headers)
        : resolved.map(option => ({
            kind: 'option',
            value: option.value,
            label: option.label,
            indented: false,
          })),
    [grouped, headers, resolved],
  );

  /**
   * One chip per distinct value, in the order the field holds them.
   *
   * The de-duplication is not cosmetic. react-select identifies a value by its
   * `value`, so `removeValue` filters *every* entry matching the one clicked — a
   * field storing the same position twice would render two chips and lose both
   * to one X. Both callers hold a set — a membership test, a roster — so the
   * second copy was never meaning anything; showing one chip is what the rule
   * actually does, and it makes the X remove exactly what it appears to.
   *
   * Nothing in this panel can write a duplicate, so this only ever reads one
   * that arrived another way — an older rule, an API write, an import.
   */
  const selected = React.useMemo(
    () =>
      Array.from(new Set(values)).map(
        (value): OptionRow => {
          const match = resolved.find(option => option.value === value);

          return {
            kind: 'option',
            value,
            // The stored id or name, which is a worse label than the record's own
            // and still says which record. `id_options` ships a null label for a
            // funnel with no title, so the catalog does this too.
            label: match?.label ?? value,
            indented: false,
          };
        },
      ),
    [resolved, values],
  );

  const held = React.useMemo(
    () => new Set(selected.map(row => row.value)),
    [selected],
  );

  /**
   * What react-select announces when a row takes focus, and what names a chip.
   *
   * A header's own text is a name, a count and a checkbox — three things a
   * screen reader reads as one word unless the name says the rest. react-select
   * v3 builds its live region from this call rather than from the row's markup,
   * so the state has to be in the string.
   */
  const nameFor = (row: MultiSelectRow) => {
    if (row.kind !== 'group') return row.label;

    const shown = visibleMembers(row, search);

    return intl.formatMessage(messages.conditionGroupOption, {
      count: shown.length,
      label: row.label,
      selected: groupSelectionCount(shown, held),
    });
  };

  /**
   * A header is spent rather than stored: it arrives as the option that was
   * acted on, and what it leaves behind is a change to its members.
   *
   * Named by react-select's own action metadata, because the header never
   * reaches the value array — `isOptionSelected` reports it already chosen, so
   * react-select takes it down its deselect path and hands back the value
   * untouched. That is what keeps a header out of the design system's "is
   * everything picked" count, which would otherwise shut the menu the moment a
   * header click completed the only group on the account.
   *
   * The incoming list is read as well, so the header is still found if a later
   * react-select stops reporting the option or starts appending it.
   */
  const handleChange = (
    next?: MultiSelectRow[] | null,
    meta?: { option?: MultiSelectRow },
  ) => {
    const chosen = next ?? [];
    const acted = meta?.option;
    const header =
      (acted && acted.kind === 'group' ? acted : undefined) ??
      chosen.find((row): row is GroupRow => row.kind === 'group');

    if (header) {
      onChange(
        toggleGroupMembers(
          selected.map(row => row.value),
          visibleMembers(header, search).map(member => member.value),
        ),
      );
      return;
    }

    onChange(chosen.map(row => row.value));
  };

  const errorId = `${id}-error`;
  const noteId = `${id}-note`;
  const describedBy = [error && errorId, note && noteId].filter(Boolean).join(' ');

  return (
    <div className={clsx(styles.conditionSelect, className)}>
      <StyledReactSelect
        inputId={id}
        label={label}
        // Both, for the reason `ConditionSelect` passes both: the design system
        // names the control from the rendered label only when the caller names
        // nothing itself, and several selects in this app pass `label` alone and
        // are unnamed to a screen reader.
        aria-label={label}
        aria-describedby={describedBy || undefined}
        options={rows}
        value={selected}
        onChange={handleChange}
        components={SELECT_COMPONENTS}
        getOptionLabel={nameFor}
        // Answered from the value react-select passes in, never from `held`
        // above. react-select rebuilds the menu inside
        // `UNSAFE_componentWillReceiveProps`, where `this.props` is still the
        // render before — so a closure over the component's own state answers
        // every row against the selection as it was *before* the click that
        // changed it, and the rows a click just chose stay unchecked until
        // something else reopens the menu. The argument is the new selection.
        //
        // A header reports as already chosen so that react-select removes rather
        // than appends it, and removing a row the value never held is a no-op —
        // which is the point. It keeps the header out of the value entirely, and
        // out of the count the design system closes the menu on.
        isOptionSelected={(row: MultiSelectRow, chosen: OptionRow[] = []) =>
          row.kind === 'group' ||
          chosen.some(picked => picked.value === row.value)
        }
        {...(grouped
          ? {
              inputValue: search,
              onInputChange: (value: string) => setSearch(value),
              // The search runs here rather than inside the row list, which is
              // built once and kept — see `buildRows`. It replaces react-select's
              // own matching, which would run over a header's text and drop every
              // one of them.
              filterOption: (option: {
                label: string;
                value: number | string;
                data?: MultiSelectRow;
              }) => (option.data ? rowMatches(option.data, search) : true),
            }
          : {})}
        // Boolean, not the message: a string `error` renders the design system's
        // own caption 24px below the control, which on a row this dense lands on
        // the control underneath. The border still turns, and the message is
        // rendered in flow below.
        error={Boolean(error)}
        isClearable
        isMulti
        isSearchable
        hideSelectedOptions={false}
        tabSelectsValue={false}
        placeholder=""
        size="small"
      />

      {note && (
        <Typography className={styles.conditionSelectNote} id={noteId} variant="body2">
          {note}
        </Typography>
      )}

      {/* `role="alert"` for the reason `ConditionSelect`'s message carries it:
          this is mounted only once a save has been refused, so its appearance is
          the event to announce, and a refused save moves no focus. */}
      {error && (
        <Typography
          className={styles.conditionSelectError}
          color="error"
          id={errorId}
          role="alert"
          variant="body2"
        >
          {error}
        </Typography>
      )}
    </div>
  );
};
